"use client";

import { useImperativeHandle, useLayoutEffect, useRef, type ComponentType, type SVGProps } from "react";

import type { SvgAnimationDirection, SvgAnimationHandle, SvgAnimationProps } from "./svg-animation.types";

export type { SvgAnimationDirection, SvgAnimationHandle, SvgAnimationProps } from "./svg-animation.types";

type Props = SvgAnimationProps & {
  svg: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * Easing over a whole single run, for an asset whose keyframes are evenly timed (a flipbook) and would
   * otherwise stop dead. Loops always run linearly, so a looping spin does not slow at every turn.
   */
  runEasing?: string;
};

/** Assets use CSS keyframes with a shared duration, zero delay, and paused/both fill.
 * Changing autoplay/direction/loop reinitializes playback. Speed changes
 * preserve position.
 */
export function SvgAnimation({
  svg: Svg,
  ref,
  svgRef: elementRef,
  autoplay = false,
  direction = "forward",
  loop = false,
  speed = 1,
  runEasing = "linear",
  ...props
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const controls = useRef<(SvgAnimationHandle & { updateSpeed: () => void }) | null>(null);
  const speedRef = useRef(1);

  useImperativeHandle(elementRef, () => svgRef.current!, [Svg]);

  useImperativeHandle(
    ref,
    () => ({
      play: () => controls.current?.play(),
      pause: () => controls.current?.pause(),
      reverse: () => controls.current?.reverse(),
      restart: (nextDirection) => controls.current?.restart(nextDirection),
    }),
    [],
  );

  useLayoutEffect(() => {
    speedRef.current = Number.isFinite(speed) && speed > 0 ? speed : 1;
    controls.current?.updateSpeed();
  }, [speed]);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg?.getAnimations) return;

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    let animations: Animation[] = [];
    let currentDirection = direction;

    const duration = (animation: Animation) => Number(animation.effect?.getTiming().duration) || 0;

    const setDirection = (nextDirection: SvgAnimationDirection) => {
      for (const animation of animations) {
        if (loop) {
          // Infinite timelines always run forward. Reflect the phase when changing
          // effect direction to keep the picture continuous at the reversal point.
          if (nextDirection !== currentDirection) {
            const length = duration(animation);
            const time = Number(animation.currentTime) || 0;
            animation.currentTime = length ? length - (time % length) : 0;
          }
          animation.effect?.updateTiming({ direction: nextDirection === "reverse" ? "reverse" : "normal" });
          animation.updatePlaybackRate(speedRef.current);
        } else {
          animation.updatePlaybackRate((nextDirection === "reverse" ? -1 : 1) * speedRef.current);
        }
      }
      currentDirection = nextDirection;
    };

    const start = (nextDirection: SvgAnimationDirection, reset = false) => {
      if (media.matches) return;
      setDirection(nextDirection);
      for (const animation of animations) {
        if (reset) animation.currentTime = !loop && nextDirection === "reverse" ? duration(animation) : 0;
        animation.play();
      }
    };

    const initialize = () => {
      if (media.matches) {
        // pause() can revive an animation that CSS just removed. Cancel instead
        // so the asset's static fallback remains in control under reduced motion.
        for (const animation of animations) animation.cancel();
        animations = [];
        return;
      }
      for (const animation of animations) animation.pause();
      // A media-query change removes/recreates CSS animations. Rediscover them
      // after style resolution instead of resuming detached Animation objects.
      animations = svg.getAnimations({ subtree: true });
      currentDirection = direction;
      for (const animation of animations) {
        animation.pause();
        animation.effect?.updateTiming({
          iterations: loop ? Infinity : 1,
          direction: loop && direction === "reverse" ? "reverse" : "normal",
          fill: "both",
          easing: loop ? "linear" : runEasing,
        });
        animation.updatePlaybackRate((!loop && direction === "reverse" ? -1 : 1) * speedRef.current);
        animation.currentTime = !loop && direction === "reverse" ? duration(animation) : 0;
      }
      if (autoplay && !media.matches) start(direction);
    };

    controls.current = {
      play: () => start("forward"),
      pause: () => {
        if (!media.matches) animations.forEach((animation) => animation.pause());
      },
      reverse: () => start(currentDirection === "forward" ? "reverse" : "forward"),
      restart: (nextDirection = "forward") => start(nextDirection, true),
      updateSpeed: () => setDirection(currentDirection),
    };
    initialize();
    media.addEventListener("change", initialize);

    return () => {
      media.removeEventListener("change", initialize);
      for (const animation of animations) {
        if (media.matches) animation.cancel();
        else animation.pause();
      }
      controls.current = null;
    };
  }, [Svg, autoplay, direction, loop, runEasing]);

  return <Svg aria-hidden="true" focusable="false" {...props} ref={svgRef} />;
}
